import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type Ref,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Loader2, PanelRightOpen, Star, Trash2, X } from "lucide-react";
import { mediaOrientation } from "@ckgo2/shared";
import type { AdminMediaResponse } from "@ckgo2/shared/types";
import { api } from "@/api/client";
import ConfirmModal from "@/components/common/ConfirmModal";
import { useAdminDestinationList } from "@/hooks/useAdminLists";
import { getUserMessage } from "@/lib/errorMessages";
import { blurStyle } from "@/lib/mediaStyle";
import { mediaOrientationLabel } from "./MediaFilters";
import MediaTagsField from "./MediaTagsField";
import type { PickerTileState } from "./picker/pickerModel";
import type { PhotoViewerProps } from "./PhotoViewer.types";
import {
  actionAvailable,
  afterDelete,
  createStarQueue,
  deleteDecision,
  legendEntries,
  nextStep,
  pickButton,
  prevStep,
  syncViewerIndex,
  viewerActionForKey,
  viewerModeOf,
  type PhotoViewerAction,
  type PhotoViewerViewMode,
  type StarQueue,
} from "./PhotoViewer.model";

/** `confirm` = dialog smazání použité fotky; `deleting` trvá až do posunu po smazání. */
export type PhotoViewerPhase = "idle" | "confirm" | "deleting" | "loading";

export interface PhotoViewerViewProps {
  item: AdminMediaResponse | undefined;
  index: number;
  /** Počet načtených fotek (`items.length`). */
  length: number;
  total: number;
  hasMore: boolean;
  mode: PhotoViewerViewMode;
  /** Optimistická ★ po stisknutí, dokud nedoběhne PATCH; jinak `undefined`. */
  starOverride: boolean | undefined;
  /** ID země → jméno; neznámé ID se vypíše jako ID. */
  countryNames: ReadonlyMap<string, string>;
  phase: PhotoViewerPhase;
  error: string | null;
  onPrev: () => void;
  onNext: () => void;
  onStar: () => void;
  onDelete: () => void;
  onDetail: () => void;
  onClose: () => void;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
  /** Po uložení tagů v panelu (`MediaTagsField`) — v obou režimech. */
  onTagsSaved: (item: AdminMediaResponse) => void;
  /** Jen v režimu `"picker"`: stav zobrazené fotky ve výběru okna; bez něj se „Vybrat“ nekreslí. */
  pickState?: PickerTileState;
  /** „Vybrat“ / „Vybráno“ v okně výběru. */
  onPick?: () => void;
  rootRef?: Ref<HTMLDivElement>;
}

function isButtonTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest("button, a[href]") !== null;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT";
}

/**
 * ★ prohlížeče: jedna fronta (`createStarQueue`) po celou dobu života prohlížeče; mapa
 * overridů ve stavu je jen její zveřejněná kopie pro render.
 */
function useStarQueue(): {
  overrides: ReadonlyMap<string, boolean>;
  queue: StarQueue<AdminMediaResponse>;
} {
  const [overrides, setOverrides] = useState<ReadonlyMap<string, boolean>>(() => new Map());
  const [queue] = useState(() =>
    createStarQueue<AdminMediaResponse>({
      save: (id, value) => api.patch(`/api/admin/upload/${id}`, { isStarred: value }),
      onOverrides: setOverrides,
    }),
  );
  return { overrides, queue };
}

/**
 * Velký prohlížeč fotek stránky Média (řez 03, F7.5) a okna výběru fotek (`mode="picker"`,
 * řez 05, F7.11). Index, seznam i donačítání drží rodič; prohlížeč volá zpětná volání
 * z `PhotoViewerProps` a hlídá klávesy, ★ a smazání. Co který režim nabízí (v okně výběru
 * „Vybrat“ místo detailu a mazání), rozhoduje `actionAvailable` pro legendu, klávesy i tlačítka;
 * tlačítko „Vybrat“ kreslí jen pohled v režimu `"picker"`.
 */
export default function PhotoViewer(props: PhotoViewerProps): ReactElement {
  // `pick` platí jen v okně výběru: tlačítko „Vybrat“ mimo režim `"picker"` pohled nekreslí.
  const { items, index, total, hasMore, pick } = props;
  const mode = viewerModeOf(props);
  const queryClient = useQueryClient();
  const item = items[index];
  // Stav fotky ve výběru se čte při každém renderu (drží ho okno), prohlížeč si ho nekopíruje.
  const pickState = item && pick ? pick.stateOf(item.id) : undefined;

  // Nejnovější props pro asynchronní větve a posluchač kláves (ten se registruje jednou).
  const propsRef = useRef(props);
  useLayoutEffect(() => {
    propsRef.current = props;
  });
  // Po zavření (nebo odpojení) už prohlížeč neposouvá index ani nezavírá podruhé.
  const activeRef = useRef(true);

  const [phase, setPhaseState] = useState<PhotoViewerPhase>("idle");
  const phaseRef = useRef<PhotoViewerPhase>("idle");
  const setPhase = (p: PhotoViewerPhase) => {
    phaseRef.current = p;
    setPhaseState(p);
  };
  const [error, setError] = useState<string | null>(null);

  const stars = useStarQueue();

  const { data: destinationData } = useAdminDestinationList();
  const countryNames = useMemo(
    () => new Map((destinationData?.data ?? []).map((d) => [d.id, d.name] as const)),
    [destinationData],
  );

  function close() {
    if (!activeRef.current) return;
    activeRef.current = false;
    propsRef.current.onClose();
  }

  function moveTo(i: number) {
    if (activeRef.current) propsRef.current.onIndexChange(i);
  }

  async function goNext() {
    const p = propsRef.current;
    const step = nextStep({
      index: p.index,
      length: p.items.length,
      total: p.total,
      hasMore: p.hasMore,
    });
    if (step === "move") moveTo(p.index + 1);
    if (step !== "load") return;
    setPhase("loading");
    setError(null);
    try {
      await p.onLoadMore();
      moveTo(p.index + 1);
    } catch (err) {
      setError(getUserMessage(err));
    } finally {
      setPhase("idle");
    }
  }

  function goPrev() {
    const p = propsRef.current;
    if (prevStep({ index: p.index }) === "move") moveTo(p.index - 1);
  }

  function toggleStar(it: AdminMediaResponse) {
    setError(null);
    stars.queue.toggle(it, {
      onSaved: (saved, value) => {
        propsRef.current.onChanged({ ...saved, isStarred: value });
        void queryClient.invalidateQueries({ queryKey: ["media", saved.id] });
      },
      onError: (err) => setError(getUserMessage(err)),
    });
  }

  // Detail až po doběhnutí ★ téže fotky: jinak by zásuvka naplnila formulář starou ★
  // (svěží keš nebo GET souběžný s PATCH) a Uložit by ji tiše vrátilo.
  function openDetail(id: string) {
    void stars.queue.settled(id).then(() => {
      if (activeRef.current) propsRef.current.onOpenDetail?.(id);
    });
  }

  async function performDelete(it: AdminMediaResponse) {
    setPhase("deleting");
    setError(null);
    try {
      await api.delete(`/api/admin/upload/${it.id}`);
    } catch (err) {
      setError(getUserMessage(err));
      setPhase("idle");
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["media", it.id] });
    // Nad seznamem PŘED odebráním: rodič ho odebere až v `onDeleted`.
    const p = propsRef.current;
    const at = p.items.findIndex((x) => x.id === it.id);
    const idx = at >= 0 ? at : p.index;
    const next = afterDelete({ index: idx, length: p.items.length, hasMore: p.hasMore });
    p.onDeleted?.(it.id);
    try {
      if (next === "close") close();
      else if (next === "stay") moveTo(idx);
      else if (next === "prev") moveTo(idx - 1);
      else {
        await p.onLoadMore();
        moveTo(idx);
      }
    } catch (err) {
      setError(getUserMessage(err));
    } finally {
      // `deleting` drží až do posunu: druhé smazání nesmí trefit fotku, která se jen mihla.
      setPhase("idle");
    }
  }

  function requestDelete(it: AdminMediaResponse) {
    if (!actionAvailable(mode, "delete") || phaseRef.current !== "idle") return;
    if (deleteDecision(it.usageCount) === "trash") void performDelete(it);
    else setPhase("confirm");
  }

  function run(action: PhotoViewerAction) {
    const busy = phaseRef.current !== "idle";
    if (action === "close") return close();
    if (!actionAvailable(viewerModeOf(propsRef.current), action)) return;
    if (busy) return;
    const it = propsRef.current.items[propsRef.current.index];
    if (action === "next") return void goNext();
    if (action === "prev") return goPrev();
    if (!it) return;
    if (action === "star") return toggleStar(it);
    if (action === "delete") return requestDelete(it);
    if (action === "detail") return openDetail(it.id);
  }

  // Posluchač kláves čte stav přes refy, ať se registruje jen jednou.
  const runRef = useRef(run);
  useLayoutEffect(() => {
    runRef.current = run;
  });
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const ev = {
        key: e.key,
        metaKey: e.metaKey,
        ctrlKey: e.ctrlKey,
        altKey: e.altKey,
        repeat: e.repeat,
        isComposing: e.isComposing,
        editableTarget: isEditableTarget(e.target),
        buttonTarget: isButtonTarget(e.target),
      };
      if (phaseRef.current === "confirm") {
        // Nad dialogem jen zavření dialogu; Enter na tlačítku dialogu kliká nativně.
        if (viewerActionForKey({ ...ev, editableTarget: false }) === "close") {
          e.preventDefault();
          setPhase("idle");
        }
        return;
      }
      // Detail otevřený nad prohlížečem: Backspace v jeho poli nesmí smazat fotku.
      if (document.querySelector("[data-media-drawer]")) return;
      const action = viewerActionForKey(ev);
      if (!action) return;
      // Akci, kterou režim nenabízí (Enter a Delete v okně výběru), prohlížeč nepohltí.
      if (!actionAvailable(viewerModeOf(propsRef.current), action)) return;
      e.preventDefault();
      runRef.current(action);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Fokus na kořen (jinak by klávesy padly do pole hledání filtrů), zámek posunu stránky.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    activeRef.current = true;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    rootRef.current?.focus();
    return () => {
      activeRef.current = false;
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, []);

  // Sladění indexu s ID zobrazené fotky (přenačtení seznamu, filtr ★, přeřazení).
  const prevIndexRef = useRef<number | null>(null);
  const currentIdRef = useRef<string | null>(null);
  useEffect(() => {
    const r = syncViewerIndex(
      { prevIndex: prevIndexRef.current, currentId: currentIdRef.current },
      { items, index },
    );
    prevIndexRef.current = r.index;
    currentIdRef.current = r.currentId;
    if (items.length === 0 && !hasMore) return close();
    if (r.index !== index) moveTo(r.index);
  }, [items, index, hasMore]);

  // Přednačtení sousedních fotek.
  useEffect(() => {
    for (const n of [items[index - 1], items[index + 1]]) {
      if (n) new Image().src = n.url;
    }
  }, [items, index]);

  return (
    <PhotoViewerView
      rootRef={rootRef}
      item={item}
      index={index}
      length={items.length}
      total={total}
      hasMore={hasMore}
      mode={mode}
      starOverride={item ? stars.overrides.get(item.id) : undefined}
      countryNames={countryNames}
      phase={phase}
      error={error}
      onPrev={() => run("prev")}
      onNext={() => run("next")}
      onStar={() => run("star")}
      onDelete={() => run("delete")}
      onDetail={() => run("detail")}
      onClose={() => run("close")}
      onConfirmDelete={() => {
        if (item && phaseRef.current === "confirm") void performDelete(item);
      }}
      onCancelDelete={() => setPhase("idle")}
      onTagsSaved={(saved) => {
        propsRef.current.onChanged(saved);
        void queryClient.invalidateQueries({ queryKey: ["media", saved.id] });
      }}
      pickState={pickState}
      onPick={() => {
        if (item && pick && pickState && !pickButton(pickState).disabled) pick.onPick(item.id);
      }}
    />
  );
}

/** Prezentace prohlížeče bez stavu (statický test); háčky E2E podle Kontraktu bod 8. */
export function PhotoViewerView({
  item,
  index,
  length,
  total,
  hasMore,
  mode,
  starOverride,
  countryNames,
  phase,
  error,
  onPrev,
  onNext,
  onStar,
  onDelete,
  onDetail,
  onClose,
  onConfirmDelete,
  onCancelDelete,
  onTagsSaved,
  pickState,
  onPick,
  rootRef,
}: PhotoViewerViewProps): ReactElement {
  const { t } = useTranslation();

  const legendLabels: Record<PhotoViewerAction, string> = {
    next: t("mediaViewer.legend.next"),
    prev: t("mediaViewer.legend.prev"),
    star: t("mediaViewer.legend.star"),
    delete: t("mediaViewer.legend.delete"),
    detail: t("mediaViewer.legend.detail"),
    close: t("mediaViewer.legend.close"),
  };

  const deletable = actionAvailable(mode, "delete");
  const starred = item ? (starOverride ?? item.isStarred) : false;
  const nextDisabled = nextStep({ index, length, total, hasMore }) === "stop";
  const prevDisabled = prevStep({ index }) === "stop";
  const loading = !item || phase === "loading" || phase === "deleting";
  const legend = legendEntries().filter((e) => actionAvailable(mode, e.action));
  const pickBtn = mode === "picker" && pickState !== undefined ? pickButton(pickState) : null;
  const barButton =
    "inline-flex h-9 w-9 items-center justify-center rounded-full text-white/90 hover:bg-white/15 disabled:opacity-30";
  const sideButton =
    "absolute top-1/2 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 disabled:opacity-20";

  return (
    <>
      <div
        ref={rootRef}
        className="fixed inset-0 z-50 flex flex-col bg-black/90 text-white outline-none"
        role="dialog"
        aria-modal="true"
        aria-label={t("mediaViewer.viewer.dialogLabel")}
        tabIndex={-1}
        data-photo-viewer=""
        data-media-id={item?.id}
        data-mode={mode}
      >
        <div className="flex items-center gap-2 px-4 py-2">
          <span className="text-sm tabular-nums text-white/80">
            {t("mediaViewer.viewer.position", { current: index + 1, total })}
          </span>
          <div className="ml-auto flex items-center gap-1">
            {pickBtn && (
              <button
                type="button"
                className={`${pickBtn.pressed ? "btn-secondary" : "btn-primary"} btn-sm mr-2`}
                data-photo-viewer-pick=""
                data-state={pickState}
                aria-pressed={pickBtn.pressed}
                title={
                  pickState === "in-gallery" ? t("mediaPicker.viewer.pickInGallery") : undefined
                }
                disabled={!item || pickBtn.disabled}
                onClick={onPick}
              >
                {t(
                  pickBtn.label === "pick"
                    ? "mediaPicker.viewer.pick"
                    : "mediaPicker.viewer.picked",
                )}
              </button>
            )}
            <button
              type="button"
              className={barButton}
              data-photo-viewer-star=""
              data-starred={starred ? "true" : "false"}
              aria-pressed={starred}
              title={starred ? t("mediaViewer.viewer.unstar") : t("mediaViewer.viewer.star")}
              aria-label={starred ? t("mediaViewer.viewer.unstar") : t("mediaViewer.viewer.star")}
              disabled={!item}
              onClick={onStar}
            >
              <Star className={`h-5 w-5 ${starred ? "fill-amber-400 text-amber-400" : ""}`} />
            </button>
            {deletable && (
              <button
                type="button"
                className={barButton}
                data-photo-viewer-delete=""
                title={t("mediaViewer.viewer.delete")}
                aria-label={t("mediaViewer.viewer.delete")}
                disabled={!item || phase !== "idle"}
                onClick={onDelete}
              >
                <Trash2 className="h-5 w-5" />
              </button>
            )}
            {actionAvailable(mode, "detail") && (
              <button
                type="button"
                className={barButton}
                data-photo-viewer-detail=""
                title={t("mediaViewer.viewer.openDetail")}
                aria-label={t("mediaViewer.viewer.openDetail")}
                disabled={!item}
                onClick={onDetail}
              >
                <PanelRightOpen className="h-5 w-5" />
              </button>
            )}
            <button
              type="button"
              className={barButton}
              data-photo-viewer-close=""
              title={t("mediaViewer.viewer.close")}
              aria-label={t("mediaViewer.viewer.close")}
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          <div className="relative flex min-h-0 flex-1 items-center justify-center px-16 pb-4">
            <button
              type="button"
              className={`${sideButton} left-3`}
              data-photo-viewer-prev=""
              title={t("mediaViewer.viewer.prev")}
              aria-label={t("mediaViewer.viewer.prev")}
              disabled={prevDisabled}
              onClick={onPrev}
            >
              <ChevronLeft className="h-7 w-7" />
            </button>
            {loading ? (
              <div className="flex flex-col items-center gap-2 text-white/70">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span className="text-sm">{t("mediaViewer.viewer.loading")}</span>
              </div>
            ) : (
              <img
                key={item.id}
                src={item.url}
                alt={item.alt}
                className="max-h-[calc(100vh-7rem)] max-w-full object-contain"
                style={blurStyle(item.blurDataUrl)}
              />
            )}
            <button
              type="button"
              className={`${sideButton} right-3`}
              data-photo-viewer-next=""
              title={t("mediaViewer.viewer.next")}
              aria-label={t("mediaViewer.viewer.next")}
              disabled={nextDisabled}
              onClick={onNext}
            >
              <ChevronRight className="h-7 w-7" />
            </button>
          </div>

          <aside className="w-full shrink-0 space-y-4 overflow-y-auto border-t border-white/10 p-4 text-sm lg:w-72 lg:border-l lg:border-t-0">
            {error && (
              <p
                data-photo-viewer-error=""
                role="alert"
                className="rounded bg-red-600/20 px-3 py-2 text-red-200"
              >
                {error}
              </p>
            )}
            {item && (
              <dl data-photo-viewer-meta="" className="space-y-2">
                <div>
                  <dt className="text-xs text-white/50">{t("mediaViewer.meta.fileName")}</dt>
                  <dd className="break-all">
                    {item.originalName || t("mediaViewer.viewer.noName")}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-white/50">{t("mediaViewer.meta.dimensions")}</dt>
                  <dd>
                    {item.width}×{item.height}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-white/50">{t("mediaViewer.meta.orientation")}</dt>
                  <dd>{mediaOrientationLabel(mediaOrientation(item.width, item.height))}</dd>
                </div>
                <div>
                  <dt className="text-xs text-white/50">{t("mediaViewer.meta.countries")}</dt>
                  <dd>
                    {item.countryIds.length === 0
                      ? t("mediaViewer.meta.noCountry")
                      : item.countryIds.map((id) => countryNames.get(id) ?? id).join(", ")}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-white/50">{t("mediaViewer.meta.source")}</dt>
                  <dd>{item.source || t("mediaViewer.meta.noSource")}</dd>
                </div>
                <div>
                  <dt className="text-xs text-white/50">{t("mediaViewer.meta.usageLabel")}</dt>
                  <dd>{t("mediaViewer.meta.usage", { count: item.usageCount })}</dd>
                </div>
              </dl>
            )}
            {item && <MediaTagsField key={item.id} item={item} onSaved={onTagsSaved} />}
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-white/50">
                {t("mediaViewer.legend.title")}
              </h3>
              <dl data-photo-viewer-legend="" className="space-y-1">
                {legend.map((e) => (
                  <div
                    key={e.action}
                    className="flex items-center gap-2"
                    data-key-action={e.action}
                    data-keys={e.keys.join(" ")}
                  >
                    <dt className="flex gap-1">
                      {e.glyphs.map((g) => (
                        <kbd
                          key={g}
                          className="min-w-[1.75rem] rounded border border-white/30 px-1.5 py-0.5 text-center font-mono text-xs"
                        >
                          {g}
                        </kbd>
                      ))}
                    </dt>
                    <dd className="text-white/80">{legendLabels[e.action]}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </aside>
        </div>
      </div>
      {phase === "confirm" && item && (
        <div
          data-media-confirm-delete=""
          data-used-count={item.usageCount}
          role="dialog"
          aria-modal="true"
          aria-label={t("mediaViewer.confirmDelete.title")}
        >
          <ConfirmModal
            isOpen
            variant="danger"
            title={t("mediaViewer.confirmDelete.title")}
            message={t("mediaViewer.confirmDelete.message", { count: item.usageCount })}
            confirmText={t("mediaViewer.confirmDelete.confirm")}
            onConfirm={onConfirmDelete}
            onClose={onCancelDelete}
          />
        </div>
      )}
    </>
  );
}
